import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import {
  AlertTriangle,
  CheckCircle2,
  ListChecks,
  PlusCircle,
  RefreshCw,
  ShieldAlert,
  ShieldCheck,
} from 'lucide-react';
import type { FileAnomaly, PreviewOutput, UpsertPreview, UpsertPreviewFile } from '../types';

/**
 * Contrôles de cohérence des fichiers : ce qui échouerait ou se perdrait à
 * l'import (formats invalides, doublons, références introuvables) — calculé
 * sans rien modifier dans l'école.
 */
export function AnomaliesPanel({ anomalies }: { anomalies: FileAnomaly[] }) {
  const errors = anomalies.filter((a) => a.severity === 'error');
  const warnings = anomalies.filter((a) => a.severity === 'warning');

  if (anomalies.length === 0) {
    return (
      <Card>
        <CardHeader icon={<ShieldCheck />} iconColor="green">
          <CardTitle className="text-base">Contrôles de cohérence</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="flex items-center gap-2 text-sm text-green-600">
            <CheckCircle2 className="h-4 w-4" />
            Aucune anomalie détectée dans vos fichiers.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader icon={<ShieldAlert />} iconColor="orange">
        <CardTitle className="text-base">Contrôles de cohérence</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">
          Ces points ont été détectés dans vos fichiers. Vous pouvez corriger les fichiers et les
          recharger, ou importer quand même : les lignes concernées seront importées partiellement
          ou ignorées (rien ne bloque le reste).
        </p>
        {[...errors, ...warnings].map((a, i) => (
          <div
            key={`${a.file}-${i}`}
            className={`rounded-md border px-3 py-2 text-sm ${
              a.severity === 'error'
                ? 'border-red-200 bg-red-50/50 dark:border-red-900 dark:bg-red-950/20'
                : 'border-amber-200 bg-amber-50/50 dark:border-amber-900 dark:bg-amber-950/20'
            }`}
          >
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant={a.severity === 'error' ? 'destructive' : 'secondary'}>
                {a.count}
              </Badge>
              <span className="font-medium">{a.label}</span>
              <span className="text-muted-foreground">— {a.message}</span>
            </div>
            {a.samples.length > 0 && (
              <p className="mt-1 text-xs text-muted-foreground">
                Ex : {a.samples.join(' · ')}
                {a.count > a.samples.length && ` … (+${a.count - a.samples.length})`}
              </p>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

/** Libellé humain de l'action appliquée aux lignes déjà présentes. */
const EXISTING_ACTION_LABELS: Record<string, string> = {
  skip: 'seront ignorées (déjà présentes)',
  update_all: 'seront mises à jour',
  update_empty_only: 'seront complétées (champs vides uniquement)',
  archive_create_new: 'seront archivées puis recréées',
  fail: 'provoqueront une erreur',
  reuse: 'seront réutilisées telles quelles',
};

function SampleList({ title, items, total }: { title: string; items: string[]; total: number }) {
  if (items.length === 0) return null;
  return (
    <details className="mt-1">
      <summary className="cursor-pointer text-xs text-muted-foreground hover:text-foreground">
        {title} ({total})
      </summary>
      <p className="mt-1 pl-4 text-xs text-muted-foreground">
        {items.join(', ')}
        {total > items.length && `, … (+${total - items.length})`}
      </p>
    </details>
  );
}

function UpsertFileLine({ file }: { file: UpsertPreviewFile }) {
  const actionLabel = EXISTING_ACTION_LABELS[file.existingAction] ?? file.existingAction;
  return (
    <div className="border-b py-3 last:border-0">
      <div className="flex items-center justify-between text-sm">
        <span className="font-medium">{file.label}</span>
        <span className="flex items-center gap-2">
          {file.toCreate > 0 && (
            <Badge variant="success" className="text-xs">
              <PlusCircle className="mr-1 h-3 w-3" />
              {file.toCreate} à créer
            </Badge>
          )}
          {file.existing > 0 && (
            <Badge variant="warning" className="text-xs">
              <RefreshCw className="mr-1 h-3 w-3" />
              {file.existing} déjà présente(s)
            </Badge>
          )}
          {file.toCreate === 0 && file.existing === 0 && (
            <span className="text-xs text-muted-foreground">rien à importer</span>
          )}
        </span>
      </div>
      {file.existing > 0 && (
        <p className="mt-1 text-xs text-muted-foreground">
          Les lignes déjà présentes {actionLabel}.
        </p>
      )}
      {file.warnings.map((w, i) => (
        <p key={i} className="mt-1 flex items-center gap-1 text-xs text-amber-600">
          <AlertTriangle className="h-3 w-3 shrink-0" />
          {w}
        </p>
      ))}
      <SampleList title="Voir les créations" items={file.samplesCreate} total={file.toCreate} />
      <SampleList title="Voir les existants" items={file.samplesExisting} total={file.existing} />
    </div>
  );
}

/** Aperçu « upsert » : ce qui sera créé vs mis à jour/ignoré, par fichier. */
export function UpsertSection({ upsert }: { upsert: UpsertPreview }) {
  const files = Object.values(upsert.files);
  if (files.length === 0) return null;

  const totals = files.reduce(
    (acc, f) => ({ create: acc.create + f.toCreate, existing: acc.existing + f.existing }),
    { create: 0, existing: 0 },
  );

  return (
    <Card>
      <CardHeader icon={<ListChecks />} iconColor="blue">
        <CardTitle className="text-base">Ce qui sera importé</CardTitle>
      </CardHeader>
      <CardContent>
        <p className="mb-2 text-sm text-muted-foreground">
          {totals.create} élément(s) seront créés, {totals.existing} existent déjà dans l’école
          (comparaison avec l’état actuel : email pour les personnes, libellé pour la structure).
        </p>
        {files.map((f) => (
          <UpsertFileLine key={f.label} file={f} />
        ))}
        {upsert.warnings.map((w, i) => (
          <p key={i} className="mt-2 flex items-center gap-1 text-xs text-amber-600">
            <AlertTriangle className="h-3 w-3 shrink-0" />
            {w}
          </p>
        ))}
      </CardContent>
    </Card>
  );
}

export function PreviewPanel({
  preview,
  upsert,
}: {
  preview: PreviewOutput;
  upsert?: UpsertPreview | null;
}) {
  return (
    <div className="space-y-6">
      {preview.errors.length === 0 ? (
        <Alert>
          <CheckCircle2 className="h-4 w-4" />
          <AlertDescription>
            Aucune erreur détectée pendant la vérification. Vous pouvez lancer l’import réel.
          </AlertDescription>
        </Alert>
      ) : (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            {preview.errors.length} problème(s) détecté(s) pendant la vérification (voir
            ci-dessous).
          </AlertDescription>
        </Alert>
      )}

      {upsert && <UpsertSection upsert={upsert} />}

      {preview.errors.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base text-destructive">Erreurs</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-1 text-sm">
              {preview.errors.map((err, i) => (
                <li key={i} className="text-muted-foreground">
                  • {err}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
